import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const EDGE_WIDTH = 25;
const THRESHOLD = 0.35;
const SHADOW_MAX_OPACITY = 0.15;

export default function SwipeBackGesture() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isStandalone, setIsStandalone] = useState(false);
  const tracking = useRef(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const pageEl = useRef(null);
  const overlayEl = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsStandalone(mq.matches || window.navigator.standalone === true);
    const handler = (e) => setIsStandalone(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Pages where swipe-back makes no sense (root-level pages)
  const isRootPage = ['/', '/dashboard', '/events', '/messages', '/profile'].includes(location.pathname);

  const onTouchStart = useCallback((e) => {
    if (!isStandalone || isRootPage) return;
    const touch = e.touches[0];
    if (touch.clientX > EDGE_WIDTH) return;

    tracking.current = true;
    startX.current = touch.clientX;
    currentX.current = touch.clientX;

    // Get the main content wrapper
    pageEl.current = document.getElementById('app-content');
    overlayEl.current = document.getElementById('swipe-overlay');

    if (pageEl.current) {
      pageEl.current.style.transition = 'none';
      pageEl.current.style.willChange = 'transform';
    }
    if (overlayEl.current) {
      overlayEl.current.style.display = 'block';
      overlayEl.current.style.transition = 'none';
    }
  }, [isStandalone, isRootPage]);

  const onTouchMove = useCallback((e) => {
    if (!tracking.current || !pageEl.current) return;

    const touch = e.touches[0];
    currentX.current = touch.clientX;
    const dx = Math.max(0, currentX.current - startX.current);
    const progress = Math.min(dx / window.innerWidth, 1);

    pageEl.current.style.transform = `translateX(${dx}px)`;

    if (overlayEl.current) {
      overlayEl.current.style.opacity = String(SHADOW_MAX_OPACITY * (1 - progress));
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!tracking.current || !pageEl.current) return;
    tracking.current = false;

    const dx = currentX.current - startX.current;
    const progress = dx / window.innerWidth;

    if (progress > THRESHOLD) {
      // Complete the swipe — animate off screen then navigate back
      pageEl.current.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)';
      pageEl.current.style.transform = `translateX(${window.innerWidth}px)`;

      if (overlayEl.current) {
        overlayEl.current.style.transition = 'opacity 0.25s ease';
        overlayEl.current.style.opacity = '0';
      }

      setTimeout(() => {
        // Reset before navigating
        if (pageEl.current) {
          pageEl.current.style.transition = 'none';
          pageEl.current.style.transform = '';
          pageEl.current.style.willChange = '';
        }
        if (overlayEl.current) {
          overlayEl.current.style.display = 'none';
        }
        navigate(-1);
      }, 250);
    } else {
      // Snap back
      pageEl.current.style.transition = 'transform 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)';
      pageEl.current.style.transform = 'translateX(0)';

      if (overlayEl.current) {
        overlayEl.current.style.transition = 'opacity 0.2s ease';
        overlayEl.current.style.opacity = '0';
      }

      setTimeout(() => {
        if (pageEl.current) {
          pageEl.current.style.transition = '';
          pageEl.current.style.transform = '';
          pageEl.current.style.willChange = '';
        }
        if (overlayEl.current) {
          overlayEl.current.style.display = 'none';
        }
      }, 200);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isStandalone) return;

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isStandalone, onTouchStart, onTouchMove, onTouchEnd]);

  if (!isStandalone) return null;

  // The dark overlay behind the sliding page (like iOS)
  return (
    <div
      id="swipe-overlay"
      style={{
        display: 'none',
        position: 'fixed',
        inset: 0,
        backgroundColor: '#000',
        opacity: 0,
        zIndex: 49,
        pointerEvents: 'none',
      }}
    />
  );
}
